import { yupResolver } from '@hookform/resolvers/yup'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, FormHelperText, IconButton, InputAdornment, OutlinedInput } from '@mui/material'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import useClientData from '../../../hooks/useClientData'
import { cnpj, cpf } from '../../../utils/mask'
import { FormControl, Label } from './styles'

export type LoginInputs = {
  cpf: string
  password: string
}

interface ILoginForm {
  focusColor: string
  onSubmitCallback: (data: LoginInputs) => void
}

const LoginForm: FC<ILoginForm> = ({ onSubmitCallback, children }) => {
  const schema = yup
    .object()
    .shape({
      cpf: yup
        .string()
        .required('CPF/CNPJ é um campo obrigatório')
        .matches(/^(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}|\d{3}\.?\d{3}\.?\d{3}-?\d{2})$/, 'CPF/CNPJ Inválido'),
      password: yup.string().required('Senha é um campo obrigatório').min(6, 'Senha deve conter mais de 6 dígitos'),
    })
    .required()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInputs>({
    resolver: yupResolver(schema),
  })

  const { clientData } = useClientData()
  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword)
  }, [showPassword])

  const handleMouseDownPassword = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }, [])

  return (
    <Box
      height="100%"
      display="flex"
      component="form"
      autoComplete="off"
      flexDirection="column"
      onSubmit={handleSubmit(onSubmitCallback)}
    >
      <Box>
        <FormControl fullWidth variant="outlined">
          <Label id="cpf-label">CPF</Label>
          <OutlinedInput
            readOnly
            fullWidth
            id="cpf"
            type="text"
            {...register('cpf')}
            error={!!errors.cpf}
            className="disabled-input"
            value={clientData.document.length === 11 ? cpf(clientData.document) : cnpj(clientData.document)}
          />
          <Box height={20} minHeight={20}>
            <FormHelperText error id="cpf-label">
              {errors.cpf?.message || ''}
            </FormHelperText>
          </Box>
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <Label id="password-label">Senha</Label>
          <OutlinedInput
            fullWidth
            id="password"
            color="success"
            error={!!errors.password}
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            {...register('password', { required: true })}
          />
          <Box height={20} minHeight={20}>
            <FormHelperText error id="password-label">
              {errors.password?.message || ''}
            </FormHelperText>
          </Box>
        </FormControl>
      </Box>
      {children}
    </Box>
  )
}

export default LoginForm
